import {
  NOM_FORMULE,
  PRIX_DUO_MENSUEL_CENTIMES,
  PRIX_MENSUEL_CENTIMES,
  prixEnEuros,
} from '@repere/core';
import { useState } from 'react';
import { InvitationAbonnement } from './InvitationAbonnement';
import { useVersionGratuite } from './regime';

/**
 * Le passage de Link à Solo ou Duo, atteignable depuis l'en-tête du dossier et « Mon
 * compte » — web et mobile partagent le même composant, il n'y a donc rien de plus à
 * poser côté coque native.
 *
 * `PresentationFormules` ne passe qu'une fois dans la vie d'un dossier, et le rappel
 * hebdomadaire arrive quand il veut. Entre les deux, un parent qui décide de s'abonner
 * n'avait aucun chemin depuis son dossier : il fallait attendre qu'une écriture soit
 * refusée, c'est-à-dire attendre un mur. Ce bloc est ce chemin — la seule sollicitation du
 * lot qui soit **demandée**, ce qui lui vaut son propre motif dans la fenêtre.
 *
 * Trois choses le tiennent :
 *
 * - **Il n'existe que pour un compte Link avéré.** `useVersionGratuite` répond faux tant
 *   que le serveur n'a pas parlé : montrer une offre à qui l'a déjà payée est la faute que
 *   ce produit ne peut pas se permettre.
 * - **Les deux formules ont la même forme.** Solo et Duo ne sont pas un palier de base et
 *   sa version supérieure — Duo, c'est Solo pris pour deux —, et un bouton mis en avant
 *   contre l'autre en ferait un classement.
 * - **Elles mènent à la même fenêtre**, où l'interrupteur Duo reste manœuvrable : ce bloc
 *   pré-remplit un choix, il ne l'enferme pas.
 */
export function ChoisirFormule() {
  const gratuit = useVersionGratuite();

  /** La formule choisie par le clic, ou `null` quand la fenêtre est fermée. */
  const [formule, setFormule] = useState<'solo' | 'duo' | null>(null);

  if (!gratuit) return null;

  return (
    <>
      <div className="dossier-offre">
        {/* L'état avant les actions : un parent doit savoir d'où il part avant de lire ce
            qu'on lui propose. Il est écrit sans reproche — Link est un régime de
            fonctionnement sans date limite, pas un retard à rattraper. */}
        <span className="dossier-offre-etat">Formule {NOM_FORMULE.link}</span>

        <button
          type="button"
          className="dossier-offre-portee"
          onClick={() => setFormule('solo')}
        >
          {NOM_FORMULE.solo}
          <span className="dossier-offre-prix">{prixEnEuros(PRIX_MENSUEL_CENTIMES)} par mois</span>
        </button>

        {/* Duo est proposé même quand l'autre parent n'a pas encore rejoint : la fenêtre
            dit alors que la place l'attend. Le masquer ici le faisait disparaître au moment
            précis où l'on s'abonne — c'est-à-dire, le plus souvent, avant que l'invitation
            ne soit acceptée. */}
        <button
          type="button"
          className="dossier-offre-portee"
          onClick={() => setFormule('duo')}
        >
          {NOM_FORMULE.duo}
          <span className="dossier-offre-prix">
            {prixEnEuros(PRIX_DUO_MENSUEL_CENTIMES)} par mois
          </span>
        </button>
      </div>

      {formule === null ? null : (
        <InvitationAbonnement
          motif="demande"
          duoParDefaut={formule === 'duo'}
          onFermer={() => setFormule(null)}
        />
      )}
    </>
  );
}
